import {
  CONTRACT_STATUSES,
  CURRENCIES,
  INVOICE_BILLING_STATUSES,
  INVOICE_FILTERABLE_FIELDS,
  INVOICE_REVIEW_STATUSES,
  MARKETPLACE_SYNC_FILTER_STATUSES,
  MONGO_FILTER_OPERATORS,
  SYNC_STATUSES,
} from '@vy/types';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import { getBillingStatusLabel } from '../invoices-page-components/InvoiceBillingStatusLabel';

const REVIEW_STATUS_LABELS = {
  Draft: 'Draft',
  InReview: 'In Review',
  Approved: 'Approved',
  Future: 'Future',
} as const;

const CONTRACT_STATUS_LABELS = {
  InReview: 'In Review',
  Active: 'Active',
  Inactive: 'Inactive',
  Expired: 'Expired',
  Terminated: 'Terminated',
  PendingTermination: 'Pending Termination',
  Error: 'Error',
} as const;

const OPERATORS_BY_TYPE = {
  text: [{ label: 'Contains', value: MONGO_FILTER_OPERATORS.REGEX }],
  number: [
    { label: 'Equals', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Greater Than', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Less than', value: MONGO_FILTER_OPERATORS.LESS_THAN },
    { label: 'Greater than or equal', value: MONGO_FILTER_OPERATORS.GREATER_THAN_OR_EQUAL },
    { label: 'Less than or equal', value: MONGO_FILTER_OPERATORS.LESS_THAN_OR_EQUAL },
  ],
  date: [
    { label: 'Is on', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Is after', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Is before', value: MONGO_FILTER_OPERATORS.LESS_THAN },
  ],
  select: [
    { label: 'Is any of', value: MONGO_FILTER_OPERATORS.IN },
    { label: 'Is not any of', value: MONGO_FILTER_OPERATORS.NOT_IN },
  ],
  boolean: [{ label: 'Is', value: MONGO_FILTER_OPERATORS.EQUALS }],
};

const getReviewStatusLabel = (key: string): string =>
  REVIEW_STATUS_LABELS[key as keyof typeof REVIEW_STATUS_LABELS] ?? key;

const getOperatorsByInputType = (inputType: keyof typeof OPERATORS_BY_TYPE) =>
  OPERATORS_BY_TYPE[inputType] ?? [];

const createFilterField = (
  id: string,
  label: string,
  inputType: keyof typeof OPERATORS_BY_TYPE,
  options?: { value: string; label: string }[],
): FilterField => ({
  id,
  label,
  inputType,
  operators: getOperatorsByInputType(inputType),
  ...(options && { options }),
});

const createBooleanField = (id: string, label: string): FilterField =>
  createFilterField(id, label, 'boolean', [{ value: 'true', label: 'Yes' }]);

const createEnumSelectField = (
  id: string,
  label: string,
  enumObject: Record<string, string | number>,
  labelMapper?: (key: string) => string,
  valueMapper?: (value: string | number) => string,
  filter?: (key: string) => boolean,
): FilterField => {
  const options = Object.entries(enumObject)
    .filter(([key]) => !filter || filter(key))
    .map(([key, value]) => ({
      value: valueMapper ? valueMapper(value) : String(value),
      label: labelMapper ? labelMapper(key) : key,
    }));

  return createFilterField(id, label, 'select', options);
};

export const createSelectFilterField = (
  id: string,
  label: string,
  options: { value: string; label: string }[],
): FilterField => createFilterField(id, label, 'select', options);

export const INVOICES_FILTER_FIELDS: FilterField[] = [
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_CONTRACT_STATUS_FIELD,
    'Contract Status',
    CONTRACT_STATUSES,
    (key) => CONTRACT_STATUS_LABELS[key as keyof typeof CONTRACT_STATUS_LABELS],
  ),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_AMOUNT_FIELD, 'Amount', 'number'),
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_BILLING_STATUS_FIELD,
    'Billing Status',
    INVOICE_BILLING_STATUSES,
    (key) => getBillingStatusLabel(key as keyof typeof INVOICE_BILLING_STATUSES),
  ),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_DUE_DATE_FIELD, 'Due Date', 'date'),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_ISSUED_AT_FIELD, 'Issue Date', 'date'),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_LAST_SENT_FIELD, 'Last Sent', 'date'),
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_CURRENCY_FIELD,
    'Currency',
    CURRENCIES,
    (key) => CURRENCIES[key as keyof typeof CURRENCIES],
  ),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_CUSTOMER_ID_FIELD, 'Customer ID', 'text'),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_NAME_FIELD, 'Period', 'text'),
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_REVIEW_STATUS_FIELD,
    'Workflow',
    INVOICE_REVIEW_STATUSES,
    getReviewStatusLabel,
    (value) => value.toString(),
    (key) => key !== INVOICE_REVIEW_STATUSES.Future,
  ),
  createBooleanField(INVOICE_FILTERABLE_FIELDS.INVOICE_OVERAGE_FIELD, 'Overage'),
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_SYNC_INTEGRATION_FIELD,
    'Sync Integration',
    SYNC_STATUSES,
    (key) => key,
    (value) => value.toString(),
  ),
  createEnumSelectField(
    INVOICE_FILTERABLE_FIELDS.INVOICE_MARKETPLACE_SYNC_FIELD,
    'MP Sync',
    MARKETPLACE_SYNC_FILTER_STATUSES,
    (key) => key,
    (value) => value.toString(),
  ),
  createBooleanField(INVOICE_FILTERABLE_FIELDS.INVOICE_MANUAL_REVIEW_FIELD, 'Manual Review'),
  createBooleanField(INVOICE_FILTERABLE_FIELDS.INVOICE_YEARLY_COMMITMENT_FIELD, 'Yearly Commitment'),
  createBooleanField(INVOICE_FILTERABLE_FIELDS.INVOICE_METERED_FIELD, 'Usage'),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_PURCHASE_ORDER_FIELD, 'PO#', 'text'),
  createFilterField(INVOICE_FILTERABLE_FIELDS.INVOICE_NOTES_FIELD, 'Memo', 'text'),
];
